import React, { useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Pagination,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Notification } from '@core/interfaces/api';
import { useNotificationsQuery } from './queries/notifications.queries';
import { useNotificationsMutation } from './queries/notifications.mutations';
import { NotificationModal } from './components/notification-modal';
import { NotificationTable } from './components/notification-table';

export interface NotificationsPageProps {
  siteId?: number | null;
  timezone?: string | null;
}

export const NotificationsPage: React.FC<NotificationsPageProps> = ({
  siteId,
  timezone,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState('createdOn');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [search, setSearch] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Notification | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Notification | null>(null);

  const { data, isLoading, error, refetch } = useNotificationsQuery({
    siteId: siteId as number,
    page,
    rowsPerPage,
    sortField,
    sortOrder,
    search,
  });

  const { createNotification, updateNotification, deleteNotification } =
    useNotificationsMutation({ siteId: siteId as number });

  const totalCount = data?.totalCount || 0;
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / rowsPerPage)),
    [totalCount, rowsPerPage],
  );

  const handleAdd = () => {
    setEditing(null);
    setShowModal(true);
  };

  const handleEdit = (n: Notification) => {
    setEditing(n);
    setShowModal(true);
  };

  const handleDelete = (n: Notification) => {
    setConfirmDelete(n);
  };

  const handleSubmit = async (payload: any) => {
    if (!siteId) return;
    if (payload.id) {
      await updateNotification(payload);
    } else {
      await createNotification(payload);
    }
  };

  const confirmDeleteAction = async () => {
    if (!confirmDelete) return;
    await deleteNotification(confirmDelete.id);
    setConfirmDelete(null);
  };

  return (
    <Card>
      <CardHeader
        title={
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
            <Typography variant="h6">
              Community Updates ({data?.totalCount || 0})
            </Typography>
            <Stack direction="row" alignItems="center" gap={2}>
              <Button variant="contained" color="primary" onClick={handleAdd}>
                Send Community Update
              </Button>
              <TextField
                placeholder="Search"
                size="small"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
              />
            </Stack>
          </Stack>
        }
      />
      <CardContent>
        <NotificationTable
          data={data?.data || []}
          isLoading={isLoading}
          error={(error as Error) || null}
          sortField={sortField}
          sortOrder={sortOrder}
          setSortField={setSortField}
          setSortOrder={setSortOrder}
          onEdit={handleEdit}
          onDelete={handleDelete}
          timezone={timezone || undefined}
        />

        <div className="flex items-center justify-between px-4 pb-4">
          <div />
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={(_, p) => setPage(p - 1)}
          />
        </div>
      </CardContent>

      <NotificationModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={async (payload) => {
          await handleSubmit(payload);
          refetch();
        }}
        siteId={siteId as number}
        initial={editing}
      />

      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete Notification</DialogTitle>
        <DialogContent>
          Do you want to delete this notification? This cannot be undone. Note this will only delete this notification from within the mobile app; push notifications have already been sent.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDeleteAction}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
